import React, { memo, useMemo, useCallback } from 'react';
import FileIcon from '@mui/icons-material/Description';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DraftTableContainer, StyledDataGrid, ActionButton } from './DynamicForm.styles.index';


export interface DraftPreview {
  id: string;
  lastUpdated: string;
  preview: Record<string, unknown>;
}

interface DraftTableProps {
  availableDrafts: DraftPreview[];
  onLoadDraft: (draftId: string) => void;
}

export const DraftTable: React.FC<DraftTableProps> = memo(({
  availableDrafts,
  onLoadDraft,
}) => {
  const handleLoadDraft = useCallback((draftId: string) => {
    try {
      if (onLoadDraft && typeof onLoadDraft === 'function') {
        onLoadDraft(draftId);
      } else {
        console.warn('onLoadDraft callback not available');
      }
    } catch (error) {
      console.error('Error in handleLoadDraft:', error);
      // Don't throw - let the parent component handle errors
    }
  }, [onLoadDraft]);

  // Memoize draft columns to avoid recreation on each render
  const draftColumns: GridColDef[] = useMemo(() => [
    {
      field: 'preview',
      headerName: 'Details',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<{ preview: string; lastUpdated: string; id: string }>) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value as string}
        </div>
      ),
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<{ preview: string; lastUpdated: string; id: string }>) => {
        const date = new Date(params.value as string);
        return date.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<{ preview: string; lastUpdated: string; id: string }>) => (
        <ActionButton
          onClick={() => handleLoadDraft(params.row.id as string)}
          variant='primary'
          icon={FileIcon}
          aria-label={`Edit draft ${params.row.id}`}
          type="button"
        >
          Edit
        </ActionButton>
      ),
    },
  ], [handleLoadDraft]);

  if (!availableDrafts || availableDrafts.length === 0) {
    return null;
  }

  return (
    <DraftTableContainer>
      <StyledDataGrid
        autoHeight
        density="comfortable"
        rows={availableDrafts.map((draft) => {
          const previewEntries = Object.entries(draft.preview)
            .filter(([key]) => !key.endsWith('_label'))
            .slice(0, 2);

          const previewText = previewEntries
            .map(([key, value]) => {
              const label = draft.preview[`${key}_label`] as string || key;
              return `${label}: ${String(value)}`;
            })
            .join(', ');

          return {
            id: draft.id,
            preview: previewText || 'Draft',
            lastUpdated: draft.lastUpdated,
          };
        })}
        columns={draftColumns}
        disableRowSelectionOnClick
        hideFooter
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
      />
    </DraftTableContainer>
  );
});

DraftTable.displayName = 'DraftTable';

